import { AICheckFinding, AICheckEntityType } from './aiChecksTypes';
import { mockRecipesV2 } from '../../../../mocks/data/recipeV2Data';
import { rangePlanningItems } from '../../../../mocks/data/rangePlanningItemsData';
import { menus } from '../../../../mocks/data/menusData';
import { ItemLifecycleStatus } from '../rangePlanningItems/rangePlanningItemsTypes';
import { duplicateItemGroups, missingDataItems, priceAnomalies } from '../../../../mocks/data/itemRationalisationData';
import { attributesAllergensClient } from '../attributesAllergens';

/** Builds the full estate-wide list of AI Recipe Checks findings from real Recipe/Item/Menu/
 * Attributes & Allergens mock data (no separate findings dataset - every check here is derived
 * live so it always agrees with what those screens show). Covers the example checks from the
 * spec that map onto data that already exists in the demo catalogue; equipment/process matching
 * is included as a single clearly-marked future-capability example since no equipment data
 * model exists yet (see Estate Management). */
const buildEstateFindings = async (): Promise<AICheckFinding[]> => {
  const findings: AICheckFinding[] = [];

  // Ingredient/item has missing allergen data
  missingDataItems
    .filter((item) => item.missingFields.includes('Allergens'))
    .forEach((item) => {
      findings.push({
        id: `missing-allergen-${item.itemNumber}`,
        checkType: 'MissingAllergenData',
        severity: 'High',
        entityType: 'Item',
        entityId: item.itemNumber,
        entityName: item.description,
        description: `${item.description} is missing allergen data.`,
      });
    });

  // Recipe/Item/Menu dietary conflicts (e.g. marked Vegan but contains dairy) - reuses the same
  // conflict-detection rules already surfaced on the Attributes & Allergens screen.
  const conflicts = await attributesAllergensClient.getConflicts();
  conflicts.forEach((conflict) => {
    findings.push({
      id: `conflict-${conflict.id}`,
      checkType: 'DietaryConflict',
      severity: 'High',
      entityType: conflict.entityType as AICheckEntityType,
      entityId: conflict.entityId,
      entityName: conflict.entityName,
      description: conflict.description,
    });
  });

  // Carbon value missing for a recipe
  mockRecipesV2
    .filter((r) => r.carbonScore == null)
    .forEach((r) => {
      findings.push({
        id: `carbon-missing-${r.id}`,
        checkType: 'CarbonMissing',
        severity: 'Medium',
        entityType: 'Recipe',
        entityId: r.id,
        entityName: r.description,
        description: `${r.description} has no carbon rating recorded.`,
      });
    });

  // Item has unusual price movement
  priceAnomalies.forEach((anomaly) => {
    findings.push({
      id: `price-anomaly-${anomaly.itemNumber}`,
      checkType: 'PriceAnomaly',
      severity: 'Medium',
      entityType: 'Item',
      entityId: anomaly.itemNumber,
      entityName: anomaly.description,
      description: `${anomaly.description} price moved from ${anomaly.previousPrice} to ${anomaly.currentPrice}.`,
    });
  });

  // Duplicate item may exist
  duplicateItemGroups.forEach((group) => {
    findings.push({
      id: `duplicate-${group.groupId}`,
      checkType: 'DuplicateItem',
      severity: 'Low',
      entityType: 'Item',
      entityId: group.suggestedPreferred,
      entityName: group.items.map((i) => i.description).join(' / '),
      description: `Possible duplicate items: ${group.items.map((i) => i.description).join(', ')}.`,
    });
  });

  // Yield looks unrealistic
  mockRecipesV2
    .filter((r) => r.yieldQuantity <= 0)
    .forEach((r) => {
      findings.push({
        id: `yield-suspicious-${r.id}`,
        checkType: 'YieldSuspicious',
        severity: 'Medium',
        entityType: 'Recipe',
        entityId: r.id,
        entityName: r.description,
        description: `${r.description} has no recorded yield quantity - check for a data entry error.`,
      });
    });

  // Recipe uses inactive supplier item
  rangePlanningItems
    .filter((item) => item.status === ItemLifecycleStatus.Inactive)
    .forEach((item) => {
      item.recipeUsage.forEach((usage) => {
        findings.push({
          id: `inactive-item-${item.itemNumber}-${usage.recipeId}`,
          checkType: 'InactiveItemUsed',
          severity: 'High',
          entityType: 'Recipe',
          entityId: usage.recipeId,
          entityName: usage.recipeName,
          description: `${usage.recipeName} uses ${item.name}, which is marked Inactive.`,
        });
      });
    });

  // lazy: single static future-capability example - no equipment/process data model exists yet
  // (see Estate Management). Upgrade path: once sites/equipment are modelled, replace this with
  // a real check comparing each recipe's required production method against site equipment.
  findings.push({
    id: 'equipment-mismatch-example',
    checkType: 'EquipmentMismatch',
    severity: 'Low',
    entityType: 'Recipe',
    entityId: 601,
    entityName: 'Cider-Braised British Pork Shoulder',
    description: 'Recipe requires a combi oven for braising - equipment matching against site equipment is not yet modelled (Estate Management bridge).',
    isFutureCapability: true,
  });

  return findings;
};

export const aiChecksClient = {
  getEstateFindings: (): Promise<AICheckFinding[]> => buildEstateFindings(),

  getFindingsForRecipe: async (recipeId: number): Promise<AICheckFinding[]> => {
    const all = await buildEstateFindings();
    return all.filter((f) => f.entityType === 'Recipe' && Number(f.entityId) === recipeId);
  },

  getFindingsForMenu: async (menuId: number): Promise<AICheckFinding[]> => {
    const all = await buildEstateFindings();
    const menu = menus.find((m) => m.id === menuId);
    if (!menu) return [];
    const recipeIds = new Set<number>();
    menu.courses.forEach((c) => c.dishes.forEach((d) => recipeIds.add(d.recipeId)));
    (menu.dayStructure ?? []).forEach((day) => day.courses.forEach((c) => c.dishes.forEach((d) => recipeIds.add(d.recipeId))));
    return all.filter((f) =>
      (f.entityType === 'Menu' && Number(f.entityId) === menuId)
      || (f.entityType === 'Recipe' && recipeIds.has(Number(f.entityId))));
  },
};
